/**
 * Telegram WebView Detection Utilities
 *
 * Provides reliable detection of Telegram WebView environment.
 * Works with both @telegram-apps/sdk-react and native Telegram Web App.
 */

// Cache the detection result since it won't change during session
let cachedResult: boolean | null = null

/**
 * Detects if the app is running inside Telegram WebView.
 *
 * Detection methods (in order of reliability):
 * 1. Check cached result (set early during app init)
 * 2. Check for Telegram SDK object (window.Telegram.WebApp)
 * 3. Check URL parameters for tgWebApp markers
 * 4. Check user agent for Telegram/WebView indicators
 * 5. Check for @telegram-apps/sdk launch params in sessionStorage
 *
 * @returns true if running inside Telegram WebView
 */
export function detectTelegramWebView(): boolean {
  // Return cached result if available
  if (cachedResult !== null) {
    return cachedResult
  }

  if (typeof window === 'undefined') {
    return false
  }

  // 1. Check for native Telegram SDK object
  const hasTelegramSDK = !!(window as any).Telegram?.WebApp

  // 2. Check URL parameters (hash or query string)
  // Telegram injects these when opening mini apps
  // Check for tgWebAppData in hash (common format: #tgWebAppData=...)
  const hash = window.location.hash
  const search = window.location.search
  const hasUrlParams =
    hash.includes('tgWebApp') ||
    search.includes('tgWebApp') ||
    hash.includes('tgWebAppData') ||
    search.includes('tgWebAppData') ||
    // Also check for encoded tgWebAppData (might be URL encoded)
    hash.includes('tgWebAppPlatform') ||
    search.includes('tgWebAppPlatform')

  // 3. Check user agent for Telegram indicators
  // Note: Also check for generic "WebView" which is common in mobile apps
  const hasTelegramUserAgent =
    navigator.userAgent.includes('Telegram') ||
    navigator.userAgent.includes('TelegramBot') ||
    navigator.userAgent.includes('WebView')

  // 4. Check for @telegram-apps/sdk launch params in sessionStorage
  // The SDK stores launch params here after initialization
  const hasTelegramAppsSDKParams = (() => {
    try {
      // Check multiple possible storage keys used by @telegram-apps/sdk
      const keys = [
        'telegram-apps/launch-params',
        '__telegram__initParams',
        'tgWebAppData',
        // @telegram-apps/sdk-react uses this key pattern
        'tapps/launchParams',
      ]
      for (const key of keys) {
        if (sessionStorage.getItem(key) || localStorage.getItem(key)) {
          return true
        }
      }
      // Also check if any sessionStorage key starts with 'telegram' or 'tg'
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && (key.toLowerCase().startsWith('telegram') || key.toLowerCase().startsWith('tg'))) {
          return true
        }
      }
    } catch {
      // Storage access might be restricted
    }
    return false
  })()

  // 5. Check if __tgWebAppPlatform is set (sometimes set by Telegram client)
  const hasPlatformVar = !!(window as any).__tgWebAppPlatform

  // 6. Check document referrer for telegram.org
  const hasTelegramReferrer =
    document.referrer.includes('telegram.org') ||
    document.referrer.includes('t.me')

  // 7. Check for __telegram__ global set by @telegram-apps/sdk
  const hasTelegramAppsGlobal = !!(window as any).__telegram__

  const result =
    hasTelegramSDK ||
    hasUrlParams ||
    hasTelegramUserAgent ||
    hasTelegramAppsSDKParams ||
    hasPlatformVar ||
    hasTelegramReferrer ||
    hasTelegramAppsGlobal

  // Cache the result
  cachedResult = result

  return result
}

/**
 * Manually set the Telegram detection result.
 * Call this early in app initialization if you detect Telegram through other means
 * (e.g., via @telegram-apps/sdk-react successful init)
 */
export function setTelegramWebViewDetected(detected: boolean): void {
  cachedResult = detected
}

/**
 * Waits for Telegram WebView environment to be ready.
 * Useful when you need to ensure the SDK is fully initialized.
 *
 * @param timeout - Maximum time to wait in ms (default: 3000)
 * @returns Promise that resolves to true if Telegram environment detected, false otherwise
 */
export async function waitForTelegramWebView(
  timeout: number = 3000,
): Promise<boolean> {
  // Check immediately
  if (detectTelegramWebView()) {
    return true
  }

  // Wait and poll for Telegram SDK to initialize
  return new Promise((resolve) => {
    const startTime = Date.now()
    const interval = setInterval(() => {
      if (detectTelegramWebView()) {
        clearInterval(interval)
        resolve(true)
      } else if (Date.now() - startTime >= timeout) {
        clearInterval(interval)
        resolve(false)
      }
    }, 100)
  })
}

/**
 * Gets Telegram WebApp instance if available.
 * Returns undefined if not in Telegram environment.
 */
export function getTelegramWebApp(): any | undefined {
  if (typeof window === 'undefined') {
    return undefined
  }
  return (window as any).Telegram?.WebApp
}
